// ---------------------------------------------------------------------------
// EditorShell — Main 3-panel layout using react-resizable-panels.
//
// Structure:
//   EditorToolbar (top)
//   Left Panel | Canvas | Right Panel (middle, resizable)
//   StatusBar (bottom)
//
// The left panel is always visible with a tabbed interface (Layers/Insert).
// No separate ActivityBar.
//
// ---------------------------------------------------------------------------

import { Panel, PanelGroup, PanelResizeHandle } from '@/lib/resizable-panels-compat'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { getPageWorkPanelCopy } from '@/features/page-editor/page-work-panel-copy'
import { bindKeyboardShortcuts } from '@/features/page-editor/interactions/keyboard/shortcuts'
import { EditorDndContext } from '@/features/page-editor/interactions/dnd'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { cn } from '@/lib/utils'
import { StatusBar } from './status-bar'
import { EditorToolbar } from '../toolbar/editor-toolbar'
import { LeftPanel } from '../library/left-panel'
import { InsertPanel } from '../library/insert-panel'
import { CanvasViewport } from '../canvas/canvas-viewport'
import { PropertyInspector } from '../inspector/property-inspector'
import { KeyboardShortcutsDialog } from './keyboard-shortcuts-dialog'
import { Toaster } from './toaster'
import { SelectionCommandBar } from '@/shell/ide-v2/selection-command-bar'
import { Blocks, Layers3, Monitor, PanelRightClose, PanelRightOpen, SlidersHorizontal, X } from 'lucide-react'

export interface EditorShellProps {
  embedded?: boolean
  inspectorMode?: 'panel' | 'drawer' | 'work-panel'
}

type PageWorkPanelTab = 'details' | 'add-ui'

export function EditorShell({ embedded = false, inspectorMode = 'panel' }: EditorShellProps) {
  const { locale: shellLocale } = useStudioLocale()
  const shellCopy = getPageWorkPanelCopy(shellLocale)
  const shellRef = useRef<HTMLDivElement>(null)
  const rightPanelOpen = useEditorStore((s) => s.rightPanelOpen)
  const isPreview = useEditorStore((s) => s.isPreview)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [inspectorDrawerOpen, setInspectorDrawerOpen] = useState(false)
  const [pageWorkPanelOpen, setPageWorkPanelOpen] = useState(false)
  const [pageWorkPanelTab, setPageWorkPanelTab] = useState<PageWorkPanelTab>('details')
  const [compactPrimaryPane, setCompactPrimaryPane] = useState<'layers' | 'canvas'>('canvas')
  const { isCompact } = useUIBreakpoint()

  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectedPageId = useEditorStore((s) => s.selectedPageId)
  const selectionTier = useEditorStore((s) => s.selectionTier)

  const toggleShortcuts = useCallback(() => setShortcutsOpen((v) => !v), [])
  const isPageWorkPanel = inspectorMode === 'work-panel' && !isPreview
  const showInlinePageWorkPanel = isPageWorkPanel && !isCompact
  const showCompactPageWorkPanel = isPageWorkPanel && isCompact
  const openPageWorkPanel = useCallback((tab: PageWorkPanelTab) => {
    setCompactPrimaryPane('canvas')
    setPageWorkPanelTab(tab)
    setPageWorkPanelOpen(true)
  }, [])

  useEffect(() => {
    return bindKeyboardShortcuts()
  }, [])

  useEffect(() => {
    if (!showCompactPageWorkPanel || isPreview) return
    const hasSelection =
      selectionTier === 'component'
        ? selectedIds.length > 0
        : selectionTier === 'block'
          ? selectedBlockName != null
          : selectedPageId != null
    if (!hasSelection) return
    setPageWorkPanelTab('details')
    setPageWorkPanelOpen(true)
  }, [isPreview, selectedBlockName, selectedIds.length, selectedPageId, selectionTier, showCompactPageWorkPanel])

  // "?" key toggles keyboard shortcuts dialog
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault()
        setShortcutsOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  return (
    <div
      ref={shellRef}
      tabIndex={-1}
      className={`flex ${embedded ? 'h-full' : 'h-screen'} flex-col bg-background text-foreground outline-none`}
    >
      {/* Top toolbar */}
      {!embedded && <EditorToolbar onOpenShortcuts={toggleShortcuts} />}

      {/* Main content area — wrapped with DnD context for block reordering */}
      <EditorDndContext>
        <div className="relative flex flex-1 overflow-hidden max-sm:flex-col">
          {inspectorMode === 'drawer' && !isPreview && (
            <PageSelectionBar
              mode="details-only"
              inspectorOpen={inspectorDrawerOpen}
              onToggleInspector={() => setInspectorDrawerOpen((open) => !open)}
            />
          )}
          {showCompactPageWorkPanel && (
            <PageSelectionBar
              mode="work-panel"
              inspectorOpen={pageWorkPanelOpen}
              panelTab={pageWorkPanelTab}
              primaryPane={compactPrimaryPane}
              onShowLayers={() => setCompactPrimaryPane('layers')}
              onShowCanvas={() => setCompactPrimaryPane('canvas')}
              onOpenDetails={() => openPageWorkPanel('details')}
              onOpenAddUi={() => openPageWorkPanel('add-ui')}
            />
          )}
          {/* Resizable 3-panel layout — no ActivityBar */}
          <PanelGroup direction="horizontal" className="flex-1">
            {/* Left panel — hidden in preview mode */}
            {!isPreview && (
              <>
                <Panel
                  defaultSize={20}
                  minSize={15}
                  maxSize={30}
                  className={compactPrimaryPane === 'layers' ? 'max-sm:block' : 'max-sm:hidden'}
                >
                  <div className="h-full" data-testid="outline-panel">
                    <LeftPanel />
                  </div>
                </Panel>
                <PanelResizeHandle className="w-1 bg-border hover:bg-ring transition-colors max-sm:hidden" />
              </>
            )}

            {/* Center — Canvas viewport */}
            <Panel
              defaultSize={
                isPreview
                  ? 100
                  : showInlinePageWorkPanel
                    ? 56
                    : inspectorMode === 'drawer' || showCompactPageWorkPanel
                      ? 80
                      : 60
              }
              className={compactPrimaryPane === 'canvas' ? 'max-sm:block' : 'max-sm:hidden'}
            >
              <CanvasViewport />
            </Panel>

            {showInlinePageWorkPanel && (
              <>
                <PanelResizeHandle className="w-1 bg-border hover:bg-ring transition-colors" />
                <Panel defaultSize={24} minSize={22} maxSize={34}>
                  <PageWorkPanel activeTab={pageWorkPanelTab} onTabChange={setPageWorkPanelTab} />
                </Panel>
              </>
            )}

            {/* Right panel — hidden in preview mode */}
            {rightPanelOpen && !isPreview && inspectorMode === 'panel' && (
              <>
                <PanelResizeHandle className="w-1 bg-border hover:bg-ring transition-colors" />
                <Panel defaultSize={20} minSize={15} maxSize={30}>
                  <div className="h-full border-l bg-background">
                    <PropertyInspector />
                  </div>
                </Panel>
              </>
            )}
          </PanelGroup>
          {inspectorMode === 'drawer' && inspectorDrawerOpen && !isPreview && (
            <div
              className="absolute bottom-3 right-3 top-3 z-30 w-[min(380px,calc(100%-32px))] overflow-hidden rounded-md border border-border bg-background shadow-xl"
              data-testid="page-inspector-drawer"
            >
              <div className="flex h-10 items-center justify-between border-b border-border px-3">
                <span className="text-sm font-semibold text-foreground">{shellCopy.detailsTab}</span>
                <button
                  type="button"
                  onClick={() => setInspectorDrawerOpen(false)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={shellCopy.closeDetails}
                >
                  <PanelRightClose className="h-4 w-4" />
                </button>
              </div>
              <div className="h-[calc(100%-2.5rem)]">
                <PropertyInspector />
              </div>
            </div>
          )}
          {showCompactPageWorkPanel && pageWorkPanelOpen && (
            <div
              className="absolute bottom-3 right-3 top-10 z-30 w-[min(400px,calc(100%-24px))] overflow-hidden rounded-md border border-border bg-background shadow-xl"
              data-testid="page-work-panel-drawer"
            >
              <PageWorkPanel
                activeTab={pageWorkPanelTab}
                onTabChange={setPageWorkPanelTab}
                onClose={() => setPageWorkPanelOpen(false)}
                floating
              />
            </div>
          )}
        </div>
      </EditorDndContext>

      {/* Bottom status bar */}
      {!embedded && <StatusBar />}

      {/* Keyboard shortcuts dialog */}
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Toast notifications */}
      {!embedded && <Toaster />}
    </div>
  )
}

function PageSelectionBar({
  inspectorOpen,
  onToggleInspector,
  mode = 'details-only',
  panelTab = 'details',
  primaryPane = 'canvas',
  onShowLayers,
  onShowCanvas,
  onOpenDetails,
  onOpenAddUi,
}: {
  inspectorOpen: boolean
  onToggleInspector?: () => void
  mode?: 'details-only' | 'work-panel'
  panelTab?: PageWorkPanelTab
  primaryPane?: 'layers' | 'canvas'
  onShowLayers?: () => void
  onShowCanvas?: () => void
  onOpenDetails?: () => void
  onOpenAddUi?: () => void
}) {
  const { locale } = useStudioLocale()
  const barCopy = getPageWorkPanelCopy(locale)
  const compactButtonClass = (active: boolean) =>
    cn(
      'inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors max-sm:gap-1 max-sm:px-1.5 max-sm:text-[11px]',
      active
        ? 'border-primary/40 bg-primary/10 text-foreground'
        : 'border-border bg-background text-foreground hover:bg-muted',
    )

  return (
    <div
      className="pointer-events-none absolute inset-x-3 top-2 z-30 flex justify-end text-xs max-sm:static max-sm:w-full max-sm:shrink-0 max-sm:justify-center max-sm:border-b max-sm:border-border max-sm:bg-card max-sm:p-1"
      data-testid="page-selection-bar"
    >
      <div className="pointer-events-auto flex h-7 max-w-full items-center rounded-md border border-border bg-card/95 px-1 shadow-sm backdrop-blur">
        {mode === 'work-panel' ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onShowLayers}
              aria-label={barCopy.layers}
              aria-pressed={primaryPane === 'layers'}
              className={compactButtonClass(primaryPane === 'layers')}
            >
              <Layers3 className="h-3.5 w-3.5" />
              {barCopy.layers}
            </button>
            <button
              type="button"
              onClick={onShowCanvas}
              aria-label={barCopy.canvas}
              aria-pressed={primaryPane === 'canvas'}
              className={compactButtonClass(primaryPane === 'canvas')}
            >
              <Monitor className="h-3.5 w-3.5" />
              {barCopy.canvas}
            </button>
            <button
              type="button"
              onClick={onOpenDetails}
              aria-label={barCopy.detailsTab}
              className={compactButtonClass(inspectorOpen && panelTab === 'details')}
            >
              <PanelRightOpen className="h-3.5 w-3.5" />
              {barCopy.detailsShort}
            </button>
            <button
              type="button"
              onClick={onOpenAddUi}
              aria-label={barCopy.addUiTab}
              className={compactButtonClass(inspectorOpen && panelTab === 'add-ui')}
            >
              <Blocks className="h-3.5 w-3.5" />
              {barCopy.addUiTab}
            </button>
          </div>
        ) : (
          <SelectionCommandBar
            compact
            showSummary={false}
            className="min-w-0 flex-nowrap overflow-x-auto whitespace-nowrap"
            detailsOpen={inspectorOpen}
            onToggleDetails={onToggleInspector}
          />
        )}
      </div>
    </div>
  )
}

function PageWorkPanel({
  activeTab,
  onTabChange,
  onClose,
  floating = false,
}: {
  activeTab: PageWorkPanelTab
  onTabChange: (tab: PageWorkPanelTab) => void
  onClose?: () => void
  floating?: boolean
}) {
  const { locale } = useStudioLocale()
  const copy = getPageWorkPanelCopy(locale)

  const tabClass = (tab: PageWorkPanelTab) =>
    cn(
      'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors',
      activeTab === tab
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    )

  return (
    <aside
      data-testid="page-work-panel"
      className={cn('flex h-full min-h-0 flex-col border-l border-border bg-background', floating && 'border-l-0')}
    >
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-border px-2">
        <button
          type="button"
          onClick={() => onTabChange('details')}
          className={tabClass('details')}
          data-testid="page-work-panel-details-tab"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {copy.detailsTab}
        </button>
        <button
          type="button"
          onClick={() => onTabChange('add-ui')}
          className={tabClass('add-ui')}
          data-testid="page-work-panel-add-tab"
        >
          <Blocks className="h-3.5 w-3.5" />
          {copy.addUiTab}
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={copy.closeWorkPanel}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {activeTab === 'details' ? (
          <div data-testid="page-inspector-panel" className="h-full">
            <PropertyInspector />
          </div>
        ) : (
          <div data-testid="page-add-ui-panel" className="h-full">
            <InsertPanel />
          </div>
        )}
      </div>
    </aside>
  )
}
